import { Dialog, Transition } from "@headlessui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import CoinsIcon from "../../assets/icons/Coins";
import TrophyColoredIcon from "../../assets/icons/TrophyColored";
import type { Prize } from "../../configs/apis/models";
import { RewardType } from "../../configs/models/constants";
import Button from "../Common/Button";

type PopupProps = {
  isVisible: boolean;
  onClose: () => void;
  prize?: Prize;
};

const Popup = ({ isVisible, onClose, prize }: PopupProps) => {
  const { t: translate } = useTranslation("translation");

  return (
    <Transition
      enter="duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="duration-300"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      show={isVisible}
    >
      <Dialog className="relative z-50" onClose={onClose}>
        <div aria-hidden="true" className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-sm overflow-hidden rounded-2xl bg-mainColor">
            <Dialog.Title className="relative">
              <div className="relative mb-16 flex justify-center">
                <div className="absolute flex w-full justify-end px-4 py-2">
                  <button className="text-3xl text-variantTextColor" onClick={onClose}>
                    &times;
                  </button>
                </div>
                <svg
                  className="text-activeColor"
                  fill="none"
                  height="130"
                  viewBox="0 0 312 130"
                  width="312"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="155.5"
                    cy="-105.5"
                    fill="currentColor"
                    r="235.5"
                  />
                </svg>
                <div className="absolute mt-16 w-fit rounded-full border-8 border-mainColor bg-activeColor p-6">
                  <TrophyColoredIcon />
                </div>
              </div>
            </Dialog.Title>
            <div className="mx-5 mb-6 flex flex-col items-center gap-4">
              <h2 className="text-center text-xl font-bold text-activeColor">
                {translate("congratulations")}
              </h2>
              <PopupCallToAction prize={prize} type={prize?.rewardType} />
              <p className="w-[30ch] text-center text-secondTextColor">
                {prize?.description || ""}
              </p>
              <Button className="!w-full" onClick={onClose}>
                {translate("ok")}
              </Button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Transition>
  );
};

const PopupCallToAction = ({
  type,
  prize,
}: {
  type: number | undefined;
  prize: Prize | undefined;
}) => {
  const { t: translate } = useTranslation("translation");
  const [isCopied, setIsCopied] = useState(false);

  // The Popup stays mounted across queued rewards, so there is no unmount to clear this.
  useEffect(() => {
    setIsCopied(false);
  }, [prize]);

  switch (type) {
  case RewardType.VOUCHER:
    return (
      <div className="flex w-full items-center justify-between rounded-xl border border-dashed border-activeColor bg-activeColor/10">
        <p className="ms-4 w-full border-activeColor p-2 font-semibold text-activeColor">
          {prize?.value}
        </p>
        <button
          className="flex gap-2 p-2 text-mainTextColor/80"
          onClick={() => {
            if (prize) {
              navigator.clipboard.writeText(prize.value);
            }
            setIsCopied(true);
          }}
        >
          <span>|</span>
          {isCopied ? translate("voucher_copied") : translate("copy")}
        </button>
      </div>
    );

  case RewardType.POINTS:
  case RewardType.POINTS_MULTIPLIER:
    return (
      <div className="flex gap-2 font-bold text-rewardDialogColor">
        <CoinsIcon />
        <div className="flex gap-1">
          <p>{prize?.value}</p>
          {translate("points")}
        </div>
      </div>
    );

  default:
    return (
      <div className="flex gap-2 font-bold text-rewardDialogColor">
        <p>{prize?.value}</p>
      </div>
    );
  }
};

export default Popup;
